import { component } from 'picoapp';
import toggleAddToCartBtn from '../../../js/lib/add-to-cart-btn';
import { addVariant } from '../../../js/lib/cart';
import Drawer from '../../../js/utils/drawer';
import formatMoney from '../../../js/utils/money';

export default component(async (node, ctx) => {
  /**
   * Data + elements
   */
  const BIS = node.querySelectorAll('.js-klaviyoBIS');
  const compareAtPrice = node.querySelector('.js-compareAtPrice');
  const fitInfoBtn = node.querySelector('.js-fitInfoBtn');
  const fitInfoContainer = node.querySelector('.js-fitInfoContainer');
  const fitSizeAddToCartBtn = node.querySelector('.js-fitSizeAddToCartBtn');
  const fitSizeSelectionBtn = node.querySelector('.js-fitSizeSelectionBtn');
  const fitSizeSelectionDrawer = node.querySelector('.js-fitSizeSelectionDrawer');
  const fitSizeSelectionDrawerCloseBtn = node.querySelector('.js-fitSizeSelectionDrawerCloseBtn');
  const fitValueBtns = node.querySelectorAll('.js-fitValueBtn');
  const form = node.querySelector('.js-form');
  const formAddToCartBtn = node.querySelector('.js-formAddToCartBtn');
  const lengthSelectionBtn = node.querySelector('.js-lengthSelectionBtn');
  const lengthSelectionContainer = node.querySelector('.js-lengthSelectionContainer');
  const mouseOverChange = node.querySelectorAll('.js-mouseOverChange');
  const linkVariantName = node.querySelector('.js-linkVariantName');
  const mobileCta = node.querySelector('.js-mobileCta');
  const price = node.querySelector('.js-price');
  const redirectGen = node.querySelector('.js-RedirectGen');
  const selectedFit = node.querySelector('.js-selectedFit');
  const selectedFitAndSize = node.querySelector('.js-selectedFitAndSize');
  const selectedLength = node.querySelector('.js-selectedLength');
  const selectedSize = node.querySelector('.js-selectedSize');
  const sizeGuideBtns = node.querySelectorAll('.js-sizeGuideBtn');
  const storeTooltipsBtns = node.querySelectorAll('.js-storeTooltipBtn');
  const storeTooltips = node.querySelectorAll('.js-storeTooltip');
  const sizeSelectionBtn = node.querySelector('.js-sizeSelectionBtn');
  const sizeSelectionContainer = node.querySelector('.js-sizeSelectionContainer');
  const fitSelectionContainer = node.querySelector('.js-fitSelectionContainer');
  const sizeSelectionList = node.querySelector('.js-sizeSelectionList');
  const stockInfo = node.querySelectorAll('.js-stockInfo');
  const stockInfoPickup = node.querySelectorAll('.js-stockInfo-pickup');
  const thumbnailBtns = node.querySelectorAll('.js-thumbnailBtn');
  const thumbnailDrawer = node.querySelector('.js-thumbnailDrawer');
  const thumbnailDrawerCloseBtn = node.querySelector('.js-thumbnailDrawerCloseBtn');
  const variantSelect = node.querySelector('.js-variantSelect');
  const eventDateBtns = node.querySelectorAll('.js-eventDateBtn');
  const selectedEventDate = node.querySelector('.js-selectedEventDate');

  const productData = JSON.parse(node.querySelector('.js-productData').innerHTML);
  const variantData = JSON.parse(node.querySelector('.js-variantData').innerHTML);

  const parseConfig = () => {
    const configScript = node.querySelector('.js-productConfig');

    if (configScript) {
      return JSON.parse(configScript.textContent || '{}');
    }

    const rawConfig = node.getAttribute('data-config') || node.dataset.config || '{}';

    try {
      return JSON.parse(rawConfig);
    } catch (error) {
      // Some templates may HTML-encode JSON strings in data attributes.
      const encodedNode = document.createElement('textarea');
      encodedNode.innerHTML = rawConfig;
      return JSON.parse(encodedNode.value);
    }
  };

  const config = parseConfig();
  const {
    fitOptionPosition,
    sizeOptionPosition,
    lengthOptionPosition,
    stockWarningLimit,
    isProductEvent,
    dateOptionPosition,
    isMultiDateEvent,
    prelaunch,
    sizeSelector,
    lengthOptionDefault,
  } = config;

  const strings = window.theme.strings.product;
  const defaultLengthValue = lengthOptionDefault || 'Standard';

  const optionLabel = (option) => {
    if (!option) return '';
    const name = typeof option === 'string' ? option : option.name;
    return String(name || '').trim().toLowerCase();
  };

  // Dot access `.length` is not the Length option, so fall back to the option
  // name on the product when the config position is missing.
  const discoverLengthPosition = (product) => {
    const options = Array.isArray(product.options) ? product.options : [];
    const match = options.find((option) => {
      const name = optionLabel(option);
      return name === 'length' || name === 'lenght';
    });

    if (!match) return null;

    if (typeof match === 'object' && Number(match.position)) return Number(match.position);

    return options.indexOf(match) + 1;
  };

  const lengthPosition = Number(lengthOptionPosition) || discoverLengthPosition(productData) || null;

  // Shared between the variant selection callback and the selector UIs, which
  // both need the per-location stock levels.
  let inventory = [];

  // Availability only means something once every option has been answered, so
  // whichever selector is active reports its own completeness here and the
  // variant callback consults it before showing any stock status.
  let isSelectionComplete = () => false;

  const updateStockStatus = (variant) => {
    setDrawerPurchaseButton(variant);

    if (!isSelectionComplete() || !variant) {
      stockInfo.forEach((info) => {
        info.classList.add('hidden');
        info.innerHTML = '';
      });

      stockInfoPickup.forEach((info) => {
        info.classList.add('hidden');

        // Only the text span is emptied; its sibling tooltip button must stay.
        const text = info.querySelector('span');

        if (text) {
          text.innerHTML = '';
        }
      });

      return;
    }

    stockInfo.forEach((info) => info.classList.remove('hidden'));

    // Owns the pickup row's visibility, which depends on the store levels.
    updateStockInfo(variant, inventory);
  };

  /**
   * Step labels echo the committed value ("Size: M"). The committed text is
   * kept on the element so a hover preview can be reverted without having to
   * recompute it.
   */
  const setStepLabel = (label, text) => {
    if (!label) return;

    label.textContent = text || '';
    label.dataset.committed = text || '';
  };

  const bindStepLabelHover = (btns, label, format = (value) => value) => {
    if (!label) return;

    btns.forEach((btn) => {
      const preview = () => {
        label.textContent = format(btn.dataset.value);
      };

      const restore = () => {
        label.textContent = label.dataset.committed || '';
      };

      btn.addEventListener('mouseenter', preview);
      btn.addEventListener('focus', preview);
      btn.addEventListener('mouseleave', restore);
      btn.addEventListener('blur', restore);
    });
  };

  /**
   * Steps reveal one at a time: the values of the step being answered plus the
   * title of the one after it, which stays locked until its turn.
   */
  const lockStep = (container, btn) => {
    if (!container) return;

    const content = container.querySelector('.js-content');

    container.classList.remove('hidden');
    container.hidden = false;

    if (btn) {
      btn.disabled = true;
    }

    if (content) {
      content.hidden = true;
    }
  };

  const unlockStep = (container, btn) => {
    if (!container) return;

    const content = container.querySelector('.js-content');

    container.classList.remove('hidden');
    container.hidden = false;

    if (btn) {
      btn.disabled = false;
    }

    if (content) {
      content.hidden = false;
    }
  };

  const hideStep = (container) => {
    if (!container) return;

    container.classList.add('hidden');
    container.hidden = true;
  };

  const uniqueOptionValues = (variants, position) => {
    if (!position) return [];

    return [...new Set(variants.map((variant) => variant[`option${position}`]).filter((value) => (
      value && value !== 'Default Title'
    )))];
  };

  const hideSizeChooserTrigger = () => {
    if (!fitSizeSelectionBtn) return;

    fitSizeSelectionBtn.hidden = true;
    fitSizeSelectionBtn.classList.add('hidden');
  };

  const updateBackInStock = ({ available }) => {
    if (!BIS) return false;

    const complete = isSelectionComplete();

    BIS.forEach((btn) => {
      btn.hidden = !complete || available;
    });

    return true;
  };

  const setDrawerPurchaseButton = (variant) => {
    if (!fitSizeAddToCartBtn) return;

    const complete = isSelectionComplete();
    const available = Boolean(complete && variant && variant.available);
    const canNotify = Boolean(fitSizeSelectionDrawer?.querySelector('.js-klaviyoBIS'));
    const notify = Boolean(complete && !available && canNotify);
    const label = fitSizeAddToCartBtn.querySelector('span');

    fitSizeAddToCartBtn.hidden = false;
    fitSizeAddToCartBtn.disabled = !complete || (!available && !notify);
    fitSizeAddToCartBtn.classList.toggle('product__size-selector-atc--notify', notify);

    if (label) {
      label.textContent = notify ? (strings.notifyMe || strings.getNotified) : strings.addToCart;
    }
  };

  const updateAddToCartBtn = (variant) => {
    setDrawerPurchaseButton(variant);
  };

  const updateEventAddToCartBtn = (variant) => {
    if (!variant || !formAddToCartBtn) return;

    // Multi-date: keep ATC / BIS hidden until a date pill is selected.
    if (!hasSelectedEventDate()) {
      resetEventPurchaseState();
      return;
    }

    const { available } = variant;
    const label = formAddToCartBtn.querySelector('span');

    formAddToCartBtn.disabled = !available;
    formAddToCartBtn.hidden = !available;

    // Sold-out dates use BIS instead of a visible sold-out ATC button.
    if (available && label) {
      label.textContent = strings.addToCart;
    }

    updateBackInStock(variant);
  };

  const hasSelectedEventDate = () => {
    if (!isMultiDateEvent) return true;

    return Array.from(eventDateBtns).some((btn) => (
      btn.classList.contains('product__size-value-button--active')
    ));
  };

  const resetEventPurchaseState = () => {
    if (!formAddToCartBtn) return;

    formAddToCartBtn.disabled = true;
    formAddToCartBtn.hidden = true;

    if (BIS) {
      BIS.forEach((btn) => {
        btn.hidden = true;
      });
    }
  };

  const findGen = (string, type) => string.split(':')[0] === type.split(':')[0];
  const arrayIsEqual = (a, b) => a.every((item) => b.includes(item))
    && b.every((item) => a.includes(item));

  const findMatchingVariant = (data, variant, type) => {
    let matchingVariant = false;
    const { options } = variant;
    const typeMatch = data.filter((item) => item.handle !== productData.handle
      && findGen(item.tag, type));

    const findVariantItem = typeMatch
      .find((item) => item.variants.filter((v) => arrayIsEqual(v.options, options)));

    // eslint-disable-next-line no-shadow,consistent-return
    findVariantItem.variants.forEach((variant) => {
      if (variant.available && arrayIsEqual(variant.options, options)) {
        matchingVariant = variant;
        return false;
      }
    });

    return matchingVariant;
  };

  const fetchData = async () => {
    const url = new URL(window.theme.routes.collectionsAll, window.location.origin);
    url.searchParams.set('view', 'data');

    return fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => data);
  };

  const updateRedirectGen = (variant) => {
    const { available } = variant;

    if (available) {
      redirectGen.hidden = true;
      return;
    }

    const { type } = redirectGen.dataset;

    if (sessionStorage.getItem('redirectGen')) {
      const redirectGenParse = JSON.parse(sessionStorage.getItem('redirectGen'));
      const url = findMatchingVariant(redirectGenParse, variant, type);
      if (url) {
        redirectGen.href = url.url;
        redirectGen.hidden = false;
      } else {
        redirectGen.hidden = true;
      }
    } else {
      fetchData().then((data) => {
        sessionStorage.setItem('redirectGen', JSON.stringify(data));
        const url = findMatchingVariant(data, variant, type);
        if (url) {
          redirectGen.href = url.url;
          redirectGen.hidden = false;
        } else {
          redirectGen.hidden = true;
        }
      });
    }
  };

  /**
   * Functions
   */

  const displayFitInfo = () => {
    if (!fitInfoContainer) return;

    fitInfoContainer.classList.toggle('product__fit-info--active');
    fitInfoBtn?.classList.toggle('product__size-selector-about-fits--active');
  };

  const initSwatches = () => {
    mouseOverChange.forEach((swatch) => {
      // Show swatch title on mouse enter
      swatch.addEventListener('mouseenter', () => {
        const { name } = swatch.dataset;
        linkVariantName.textContent = name;
      });

      // Switch back to current title on mouse leave
      swatch.addEventListener('mouseleave', () => {
        linkVariantName.textContent = linkVariantName.dataset.initialName;
      });
    });
  };

  const getVariantInventoryQuantity = (variant) => {
    if (variant.inventory_quantity) {
      return variant.inventory_quantity;
    }

    const { inventoryQuantity } = variantData.find((v) => v.id === variant.id);
    return inventoryQuantity;
  };

  const updateStockInfo = (variant, inventory) => {
    let pickupStoreLevel, onlineStoreLevel;
    let onlyOneStore = false;
    inventory.forEach((inventoryVariant) => {
      if (inventoryVariant.id.includes(variant.id)) {
        const warehouseInventory = inventoryVariant?.inventory_quantities?.find((i) => i.name.includes("Warehouse"));
        onlineStoreLevel = warehouseInventory?.level;
        if (inventoryVariant.inventory_quantities.length == 1) {
          onlyOneStore = true;
        } else {
          const pickupInventory = inventoryVariant?.inventory_quantities?.find((i) => i.name.includes("Pilestræde"));
          pickupStoreLevel = pickupInventory?.level;
        }
      }
    })

    replaceAvailabilityText(stockInfo, onlineStoreLevel, strings, false); // online store

    // 0 is a real sold-out level. Only hide pickup when that location is absent.
    const hasPickupStore = !onlyOneStore && pickupStoreLevel != null;

    stockInfoPickup.forEach((stockInfoNode) => {
      if (hasPickupStore) {
        stockInfoNode.classList.remove('hidden');
        stockInfoNode.style.display = '';
      } else {
        stockInfoNode.classList.add('hidden');
        stockInfoNode.style.display = 'none';
      }
    });

    if (!hasPickupStore) return;

    const availabilityStrings = {
      inStock: strings.inStockStore,
      soldOut: strings.soldOutStore,
      stockWarning: strings.stockWarningStore
    }

    replaceAvailabilityText(stockInfoPickup, pickupStoreLevel, availabilityStrings, true);

    function replaceAvailabilityText(container, inventoryQuantity, availabilityStrings, useSpan) {
      const soldOut = inventoryQuantity <= 0;
      const warn = inventoryQuantity <= stockWarningLimit;

      container.forEach((info) => {
        if (soldOut) {
          info.classList.remove('text-orange', 'text-green');
          info.classList.add('text-red');
        } else {
          info.classList.remove('text-red');
          info.classList[warn ? 'add' : 'remove']('text-orange');
          info.classList[warn ? 'remove' : 'add']('text-green');
        }
        let text = availabilityStrings.inStock;
        if (soldOut) {
          text = availabilityStrings.soldOut;
        } else if (warn) {
          text = availabilityStrings.stockWarning.replace('%quantity%', inventoryQuantity);
        }

        let infoText = info;
        if (useSpan) {
          infoText = info.querySelector("span");
        }

        infoText.innerHTML = `<div class="w-[14px] h-[14px] rounded-full bg-current"></div><p class="text-black stock-text">${text}</p>`;
      })
    }
  };

  async function getInventory(productData) {
    const storeLang = window.theme.storeUrl.split('.')[window.theme.storeUrl.split('.').length - 1];
    const storeMap = {
      'se': 'barons-swe',
      'nl': 'barons-nl',
      'no': 'barons-no',
      'dk': 'barons-dk',
      'com': 'barons-dk'
    };

    const productId = productData.id;
    const variantCount = productData.variants.length;
    const inventoryEndpoint = `https://netlify.blacklemon.dk/.netlify/functions/getProductInventoryLevels?store=${storeMap[storeLang]}&prodId=${productId}&variantCount=${variantCount}`;

    try {
      const inventory = await fetch(inventoryEndpoint);
      const inventoryData = await inventory.json();
      return inventoryData;
    } catch (error) {
      console.error('Error fetching inventory', error);
    }
  }

  async function initVariantSelection() {

    inventory = await getInventory(productData) || [];
    const selectedVariantCallback = (variant) => {
      // Runs even for an unresolvable combination, so stale status is cleared.
      updateStockStatus(variant);

      if (variant) {
        if (compareAtPrice) {
          if (variant.compare_at_price > variant.price) {
            compareAtPrice.textContent = formatMoney(variant.compare_at_price, theme.moneyFormat); // eslint-disable-line
          } else {
            compareAtPrice.textContent = '';
          }
        }

        price.textContent = formatMoney(variant.price, theme.moneyFormat);
        updateAddToCartBtn(variant);
        updateBackInStock(variant);

        if (redirectGen) {
          updateRedirectGen(variant);
        }

      }
    };

    const createShopifySelection = () => new Shopify.OptionSelectors(`product-${productData.id}`, {
      product: productData,
      onVariantSelected: selectedVariantCallback,
      enableHistoryState: true,
    });

    createShopifySelection();
  };

  /**
   * Simple selector: option titles are visible from the start. Buttons unlock
   * one row at a time. A row with a single value is preselected and hidden.
   */
  const initSimpleOptionSelection = () => {
    // Append drawer to body to reset z-index and position
    document.body.appendChild(fitSizeSelectionDrawer);
    const drawer = Drawer(fitSizeSelectionDrawer);

    fitSizeSelectionBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      drawer.open();
    });
    fitSizeSelectionDrawer.addEventListener('click', (e) => {
      if (e.target.closest('.klaviyo-bis-trigger')) return;
      e.stopPropagation();
    });
    fitSizeSelectionDrawerCloseBtn.addEventListener('click', () => drawer.close());

    // Scoped to the drawer rather than the component, because the drawer has
    // just been moved out of it and no longer matches node queries.
    const rows = Array.from(fitSizeSelectionDrawer.querySelectorAll('.js-simpleSizeRow'));
    const trueOptionSelectors = node.querySelectorAll('.single-option-selector');
    const selections = new Map();
    const triggerLabel = selectedFitAndSize.textContent;
    let hasExplicitSelection = false;

    const rowBtns = (row) => Array.from(row.querySelectorAll('.js-simpleSizeValueBtn'));

    const selectorFor = (position) => Array.from(trueOptionSelectors).find((selector) => (
      selector.dataset.option === `option${position}`
    ));

    // A variant matching every row that has been picked so far. With no rows
    // (single-variant product) this resolves to the only variant.
    const findVariant = () => productData.variants.find((variant) => rows.every((row) => (
      variant[`option${row.dataset.optionPosition}`] === selections.get(row.dataset.optionPosition)
    )));

    // No rows means there is nothing to choose, which is only ever true of a
    // single-variant product.
    isSelectionComplete = () => {
      if (!rows.length) return productData.variants.length === 1;

      return rows.every((row) => selections.has(row.dataset.optionPosition));
    };

    // A value is purchasable when some available variant carries it alongside
    // everything else already picked, so combinations narrow as rows are filled.
    const isValueAvailable = (position, value) => productData.variants.some((variant) => {
      if (!variant.available) return false;
      if (variant[`option${position}`] !== value) return false;

      return rows.every((row) => {
        const rowPosition = row.dataset.optionPosition;

        if (rowPosition === position) return true;

        const selected = selections.get(rowPosition);

        return !selected || variant[`option${rowPosition}`] === selected;
      });
    });

    const refreshAvailability = () => {
      rows.forEach((row) => {
        const position = row.dataset.optionPosition;

        rowBtns(row).forEach((btn) => {
          const available = isValueAvailable(position, btn.dataset.value);
          const text = btn.querySelector('.js-simpleSizeValueText');

          text.classList.toggle('product__strikethrough', !available);
        });
      });
    };

    const updatePurchaseState = () => {
      const complete = isSelectionComplete();
      const variant = complete ? findVariant() : null;
      const available = Boolean(complete && variant && variant.available);

      setDrawerPurchaseButton(variant);

      // Only offer back-in-stock once the shopper has named a combination.
      BIS.forEach((bisBtn) => {
        bisBtn.hidden = !complete || available;
      });

      updateStockStatus(variant);
    };

    // Keep the Liquid title until the shopper picks something themselves.
    // Preselected hidden values join the label only after that.
    const updateTriggerLabel = () => {
      if (!hasExplicitSelection) {
        selectedFitAndSize.textContent = triggerLabel;
        return;
      }

      const values = rows
        .map((row) => selections.get(row.dataset.optionPosition))
        .filter(Boolean);

      selectedFitAndSize.textContent = values.length ? values.join(' ') : triggerLabel;
    };

    const resetRowsAfter = (changedRow) => {
      const start = rows.indexOf(changedRow);

      rows.forEach((row, index) => {
        if (index <= start) return;

        selections.delete(row.dataset.optionPosition);
        rowBtns(row).forEach((rowBtn) => {
          rowBtn.classList.remove(
          'product__simple-size-button--active',
          'product__fit-option-button--active',
          'product__size-option-button--active',
        );
        });
        setStepLabel(row.querySelector('.js-simpleSizeLabel'), '');
      });
    };

    const previousRowsSelected = (index) => rows.slice(0, index).every((row) => (
      selections.has(row.dataset.optionPosition)
    ));

    const syncRowVisibility = () => {
      rows.forEach((row, index) => {
        const content = row.querySelector('.js-content');
        const singleton = rowBtns(row).length <= 1;

        if (singleton) {
          hideStep(row);
          return;
        }

        row.classList.remove('hidden');
        row.hidden = false;

        if (!content) return;

        content.hidden = index > 0 && !previousRowsSelected(index);
      });
    };

    const preferredButton = (row) => {
      const btns = rowBtns(row);

      if (btns.length === 1) return btns[0];

      const isLengthRow = parseInt(row.dataset.optionPosition, 10) === lengthPosition;

      if (!isLengthRow) return null;

      return btns.find((btn) => (
        btn.dataset.value === defaultLengthValue || btn.dataset.value === 'Standard'
      )) || null;
    };

    const autoFillUnlockedRows = () => {
      rows.forEach((row, index) => {
        if (selections.has(row.dataset.optionPosition)) return;
        if (index > 0 && !previousRowsSelected(index)) return;

        const btn = preferredButton(row);

        if (btn) selectValue(row, btn, { fromAuto: true });
      });

      syncRowVisibility();
    };

    const selectValue = (row, btn, { fromAuto = false } = {}) => {
      const position = row.dataset.optionPosition;
      const { value } = btn.dataset;
      const previous = selections.get(position);

      if (!fromAuto) {
        hasExplicitSelection = true;
      }

      if (previous !== value) {
        resetRowsAfter(row);
      }

      selections.set(position, value);

      rowBtns(row).forEach((rowBtn) => {
        const isSelected = rowBtn === btn;

        ['product__fit-option-button', 'product__size-option-button', 'product__simple-size-button'].forEach((base) => {
          rowBtn.classList.toggle(`${base}--active`, isSelected && rowBtn.classList.contains(base));
        });
      });

      setStepLabel(row.querySelector('.js-simpleSizeLabel'), value);
      updateTriggerLabel();

      const selector = selectorFor(position);

      if (selector) {
        selector.value = value;
      }

      if (!fromAuto) {
        syncRowVisibility();
        autoFillUnlockedRows();
      }

      // Shopify's OptionSelectors treats every hidden select as chosen, so a
      // partial pick would otherwise resolve a default combination and show
      // Add to cart. Only sync the variant once every row has an answer.
      if (isSelectionComplete()) {
        trueOptionSelectors.forEach((optionSelector) => optionSelector.dispatchEvent(new Event('change', { bubbles: true })));
      }

      refreshAvailability();
      updatePurchaseState();
    };

    rows.forEach((row) => {
      const btns = rowBtns(row);

      btns.forEach((btn) => {
        btn.addEventListener('click', () => selectValue(row, btn));
      });

      bindStepLabelHover(btns, row.querySelector('.js-simpleSizeLabel'));
    });

    syncRowVisibility();
    autoFillUnlockedRows();

    if (rows.every((row) => rowBtns(row).length <= 1)) {
      hideSizeChooserTrigger();
    } else if (fitSizeSelectionBtn) {
      fitSizeSelectionBtn.hidden = false;
      fitSizeSelectionBtn.classList.remove('hidden');
    }

    refreshAvailability();
    updatePurchaseState();
  };

  const initOptionSelection = () => {
    // Append drawer to body to reset z-index and position
    document.body.appendChild(fitSizeSelectionDrawer);
    const drawer = Drawer(fitSizeSelectionDrawer);
    fitSizeSelectionBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      drawer.open();
    });
    fitSizeSelectionDrawer.addEventListener('click', (e) => {
      if (e.target.closest('.klaviyo-bis-trigger')) return;
      e.stopPropagation();
    });
    fitSizeSelectionDrawerCloseBtn.addEventListener('click', () => drawer.close());

    const trueOptionSelectors = node.querySelectorAll('.single-option-selector');
    const selectorForPosition = (position) => {
      if (!position) return null;

      const selectors = Array.from(trueOptionSelectors);
      const positionNumber = Number(position);
      const matched = selectors.find((selector) => {
        const raw = selector.getAttribute('data-option') || '';
        const digits = raw.match(/\d+/);

        return digits && Number(digits[0]) === positionNumber;
      });

      return matched || selectors[positionNumber - 1] || null;
    };
    const trueFitSelector = selectorForPosition(fitOptionPosition);
    const trueSizeSelector = selectorForPosition(sizeOptionPosition);
    const trueLengthSelector = selectorForPosition(lengthPosition);

    let pickedFit = null;
    let pickedLength = null;
    let pickedSize = null;
    let hasExplicitSelection = false;
    const triggerLabel = selectedFitAndSize
      ? selectedFitAndSize.textContent
      : (window.theme.hasFit ? strings.selectFitAndSize : strings.selectedSize);

    const optionIsPresent = (position) => uniqueOptionValues(productData.variants, position).length > 0;

    // Size is optional. A product with only Fit (e.g. Casual) must still resolve
    // a variant. Length is only a real choice when more than one value applies.
    isSelectionComplete = () => {
      if (trueFitSelector && optionIsPresent(fitOptionPosition) && !pickedFit) return false;
      if (trueSizeSelector && optionIsPresent(sizeOptionPosition) && !pickedSize) return false;
      if (lengthPosition && optionIsPresent(lengthPosition) && !pickedLength) return false;

      return true;
    };

    const matchingVariants = ({ fit = pickedFit, size = pickedSize } = {}) => (
      productData.variants.filter((variant) => {
        if (fitOptionPosition && fit && variant[`option${fitOptionPosition}`] !== fit) return false;
        if (sizeOptionPosition && size && variant[`option${sizeOptionPosition}`] !== size) return false;

        return true;
      })
    );

    const commitVariant = () => {
      if (isSelectionComplete()) {
        trueOptionSelectors.forEach((selector) => selector.dispatchEvent(new Event('change', { bubbles: true })));
      } else {
        updateStockStatus(null);
      }
    };

    const updateTriggerLabel = () => {
      if (!hasExplicitSelection) {
        selectedFitAndSize.textContent = triggerLabel;
        return;
      }

      const values = [pickedFit, pickedSize, pickedLength].filter(Boolean);

      selectedFitAndSize.textContent = values.length ? values.join(' ') : triggerLabel;
    };

    const chooserIsNeeded = () => (
      uniqueOptionValues(productData.variants, fitOptionPosition).length > 1
      || uniqueOptionValues(productData.variants, sizeOptionPosition).length > 1
      || uniqueOptionValues(productData.variants, lengthPosition).length > 1
    );

    const lengthButtonEls = () => (
      lengthSelectionContainer
        ? Array.from(lengthSelectionContainer.querySelectorAll('.js-lengthValueBtn'))
        : []
    );

    const clearLengthActive = () => {
      lengthButtonEls().forEach((btn) => {
        btn.classList.remove(
          'product__fit-option-button--active',
          'product__size-option-button--active',
          'product__length-value-button--active',
        );
      });
    };

    const paintLengthButtons = (lengths) => {
      const pool = matchingVariants();

      lengthButtonEls().forEach((btn) => {
        const { value } = btn.dataset;
        const item = btn.closest('.js-lengthValueBtnListItem');
        const text = btn.querySelector('.js-lengthValueText');
        const available = pool.some((variant) => (
          variant[`option${lengthPosition}`] === value && variant.available
        ));

        if (item) item.classList.toggle('hidden', !lengths.includes(value));
        text?.classList.toggle('product__strikethrough', !available);
      });
    };

    const rememberLength = (value) => {
      if (value) {
        if (trueLengthSelector) trueLengthSelector.value = value;
        localStorage.setItem('selectedLength', value);
        return;
      }

      if (trueLengthSelector) trueLengthSelector.value = '';
      localStorage.removeItem('selectedLength');
    };

    const refreshLengthStep = () => {
      const allLengths = uniqueOptionValues(productData.variants, lengthPosition);

      if (!lengthPosition || !allLengths.length || !lengthSelectionContainer) {
        hideStep(lengthSelectionContainer);
        pickedLength = null;
        return;
      }

      const fitChoices = uniqueOptionValues(productData.variants, fitOptionPosition);
      const waitingOnFit = Boolean(trueFitSelector && fitChoices.length > 1 && !pickedFit);

      // Fit, then size, then length. Length stays hidden while fit is the step
      // being answered.
      if (waitingOnFit) {
        hideStep(lengthSelectionContainer);
        pickedLength = null;
        rememberLength(null);
        clearLengthActive();
        setStepLabel(selectedLength, '');
        return;
      }

      const sizesForFit = uniqueOptionValues(matchingVariants({ size: null }), sizeOptionPosition);
      const waitingOnSize = Boolean(trueSizeSelector && sizesForFit.length > 1 && !pickedSize);

      if (waitingOnSize) {
        const upcomingLengths = uniqueOptionValues(matchingVariants({ size: null }), lengthPosition);

        pickedLength = null;
        rememberLength(null);
        clearLengthActive();
        setStepLabel(selectedLength, '');

        if (upcomingLengths.length > 1) {
          lockStep(lengthSelectionContainer, lengthSelectionBtn);
        } else {
          hideStep(lengthSelectionContainer);
        }

        return;
      }

      const lengths = uniqueOptionValues(matchingVariants(), lengthPosition);

      if (lengths.length <= 1) {
        hideStep(lengthSelectionContainer);
        pickedLength = lengths[0] || null;
        rememberLength(pickedLength);
        clearLengthActive();
        setStepLabel(selectedLength, '');
        updateTriggerLabel();
        return;
      }

      unlockStep(lengthSelectionContainer, lengthSelectionBtn);
      paintLengthButtons(lengths);

      if (!pickedLength || !lengths.includes(pickedLength)) {
        pickedLength = lengths.includes(defaultLengthValue) ? defaultLengthValue : null;
      }

      lengthButtonEls().forEach((btn) => {
        btn.classList.toggle('product__fit-option-button--active', btn.dataset.value === pickedLength);
      });

      rememberLength(pickedLength);
      setStepLabel(selectedLength, pickedLength || '');
      updateTriggerLabel();
    };

    const renderSizeButtons = (fitPool) => {
      if (!sizeSelectionList) return;

      const uniqueMatchingVariants = [...new Map(fitPool.map((item) => (
        [item[`option${sizeOptionPosition}`], item]
      ))).values()];

      sizeSelectionList.innerHTML = uniqueMatchingVariants.map((variant) => {
        const { inventoryQuantity } = variantData.find((v) => v.id === variant.id) || {};
        const sizeValue = variant[`option${sizeOptionPosition}`];
        const sizeAvailable = fitPool.some((item) => (
          item[`option${sizeOptionPosition}`] === sizeValue && item.available
        ));
        const sizeClass = sizeAvailable ? '' : 'product__strikethrough';
        const activeClass = sizeValue === pickedSize ? 'product__size-option-button--active' : '';
        const safeSizeValue = String(sizeValue)
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/</g, '&lt;');

        return `<li>
          <button type="button" class="product__size-option-button js-sizeValueBtn ${activeClass}" data-value="${safeSizeValue}" data-quantity="${inventoryQuantity}">
            <span class="${sizeClass}">${safeSizeValue}</span>
          </button>
        </li>`;
      }).join('');

      const sizeValueBtns = sizeSelectionList.querySelectorAll('.js-sizeValueBtn');

      bindStepLabelHover(sizeValueBtns, selectedSize);

      sizeValueBtns.forEach((btn) => {
        btn.addEventListener('click', () => applySize(btn.dataset.value));
      });
    };

    const refreshSizeStep = () => {
      if (!trueSizeSelector) {
        hideStep(sizeSelectionContainer);
        pickedSize = null;
        refreshLengthStep();
        return;
      }

      const fitPool = matchingVariants({ size: null });
      const sizes = uniqueOptionValues(fitPool, sizeOptionPosition);

      if (sizes.length <= 1) {
        hideStep(sizeSelectionContainer);
        pickedSize = sizes[0] || null;

        if (pickedSize) {
          trueSizeSelector.value = pickedSize;
          setStepLabel(selectedSize, pickedSize);
          localStorage.setItem('selectedSize', pickedSize);
        } else {
          setStepLabel(selectedSize, '');
          localStorage.removeItem('selectedSize');
        }

        updateTriggerLabel();
        refreshLengthStep();
        return;
      }

      unlockStep(sizeSelectionContainer, sizeSelectionBtn);
      renderSizeButtons(fitPool);
      refreshLengthStep();
    };

    const applySize = (value) => {
      hasExplicitSelection = true;
      pickedSize = value;

      if (trueSizeSelector) {
        trueSizeSelector.value = value;
      }

      const sizeValueBtns = sizeSelectionList
        ? sizeSelectionList.querySelectorAll('.js-sizeValueBtn')
        : [];

      sizeValueBtns.forEach((sizeValueBtn) => {
        sizeValueBtn.classList.toggle('product__size-option-button--active', sizeValueBtn.dataset.value === value);
      });

      setStepLabel(selectedSize, value);
      localStorage.setItem('selectedSize', value);
      updateTriggerLabel();
      refreshLengthStep();
      commitVariant();
    };

    const applyFit = (value, { fromAuto = false } = {}) => {
      if (!fromAuto) {
        hasExplicitSelection = true;
      }

      pickedFit = value;
      pickedSize = null;
      pickedLength = null;
      localStorage.removeItem('selectedSize');
      localStorage.removeItem('selectedLength');
      setStepLabel(selectedSize, '');
      setStepLabel(selectedLength, '');

      if (trueFitSelector) {
        trueFitSelector.value = value;
      }

      setStepLabel(selectedFit, value);

      fitValueBtns.forEach((fitValueBtn) => {
        fitValueBtn.classList.toggle('product__fit-option-button--active', fitValueBtn.dataset.value === value);
      });

      localStorage.setItem('selectedFit', value);
      updateTriggerLabel();
      refreshSizeStep();
      commitVariant();
    };

    const selectLength = (btn) => {
      const { value } = btn.dataset;

      hasExplicitSelection = true;
      pickedLength = value;
      rememberLength(value);

      lengthButtonEls().forEach((lengthValueBtn) => {
        lengthValueBtn.classList.toggle('product__fit-option-button--active', lengthValueBtn === btn);
      });

      setStepLabel(selectedLength, value);
      updateTriggerLabel();
      commitVariant();
    };

    bindStepLabelHover(fitValueBtns, selectedFit);
    bindStepLabelHover(lengthButtonEls(), selectedLength);

    lengthSelectionContainer?.addEventListener('click', (event) => {
      const btn = event.target.closest('.js-lengthValueBtn');

      if (!btn) return;

      selectLength(btn);
    });

    fitValueBtns.forEach((btn) => {
      btn.addEventListener('click', () => applyFit(btn.dataset.value));
    });

    if (!chooserIsNeeded()) {
      hideSizeChooserTrigger();
    }

    const fits = uniqueOptionValues(productData.variants, fitOptionPosition);

    if (trueFitSelector && fits.length > 1) {
      lockStep(sizeSelectionContainer, sizeSelectionBtn);
      refreshLengthStep();
    } else if (trueFitSelector && fits.length === 1) {
      hideStep(fitSelectionContainer);
      applyFit(fits[0], { fromAuto: true });
    } else {
      hideStep(fitSelectionContainer);
      refreshSizeStep();
      commitVariant();
    }
  };

  const toggleAllAddToCartBtns = (active) => {
    [formAddToCartBtn, fitSizeAddToCartBtn].forEach((btn) => {
      toggleAddToCartBtn(btn, active);
    });
  };

  const initThumbnailDrawer = () => {
    if (thumbnailDrawer) {
      document.body.appendChild(thumbnailDrawer);
      const drawer = Drawer(thumbnailDrawer);
      thumbnailBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          drawer.open();
        });
      });
      thumbnailDrawer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      thumbnailDrawerCloseBtn.addEventListener('click', () => drawer.close());
    }
  };

  const initSizeGuideTriggers = () => {
    const sizeGuideDrawer = document.querySelector('.js-sizeGuideDrawer');

    if (sizeGuideDrawer) return;

    sizeGuideBtns.forEach((btn) => {
      btn.hidden = true;
    });
  };

  const initStoreTooltips = () => {
    const openTooltip = (tooltip) => {
      tooltip.hidden = false;

      const rect = tooltip.getBoundingClientRect();

      if (rect.left + rect.width > window.innerWidth) {
        tooltip.classList.add('product__store__tooltip--right');
      }
    };

    const closeTooltip = (tooltip) => {
      tooltip.hidden = true;
      tooltip.classList.remove('product__store__tooltip--right');
    };

    storeTooltipsBtns.forEach((btn) => {
      const { index } = btn.dataset;
      const matchingTooltip = Array.from(storeTooltips).find((tooltip) => (
        tooltip.dataset.index === index
      ));

      btn.addEventListener('mouseenter', () => {
        openTooltip(matchingTooltip);
      });

      window.addEventListener('mouseover', (event) => {
        if (!matchingTooltip.contains(event.target) && !btn.contains(event.target)) {
          closeTooltip(matchingTooltip);
        }
      });
    });
  };

  const initForm = () => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();

      toggleAllAddToCartBtns(false);

      const variantId = variantSelect.value;

      // Match original PDP ATC: open the drawer unless this combination can
      // actually be purchased. Sold-out / notify-me must not add to cart.
      const canAdd = isSelectionComplete()
        && fitSizeAddToCartBtn
        && !fitSizeAddToCartBtn.disabled
        && !fitSizeAddToCartBtn.classList.contains('product__size-selector-atc--notify');

      if (!canAdd) {
        fitSizeSelectionBtn.click();
        toggleAllAddToCartBtns(true);
        return;
      }

      addVariant({
        variantId,
        quantity: 1,
        callback: () => toggleAllAddToCartBtns(true),
      });
    });
  };

  const initEventVariantSelection = () => {
    const selectedVariantCallback = (variant) => {
      if (!variant) return;

      if (compareAtPrice) {
        if (variant.compare_at_price > variant.price) {
          compareAtPrice.textContent = formatMoney(variant.compare_at_price, theme.moneyFormat); // eslint-disable-line
        } else {
          compareAtPrice.textContent = '';
        }
      }

      if (price) {
        price.textContent = formatMoney(variant.price, theme.moneyFormat);
      }

      updateEventAddToCartBtn(variant);
    };

    // Legacy: one product = one date (single variant / Default Title).
    // Skip OptionSelectors and just sync ATC / BIS for that variant.
    if (!isMultiDateEvent) {
      const params = new URLSearchParams(window.location.search);
      const variantParam = parseInt(params.get('variant'), 10);
      const variant = productData.variants.find((v) => v.id === variantParam)
        || productData.variants.find((v) => v.available)
        || productData.variants[0];

      if (variant && variantSelect) {
        variantSelect.value = String(variant.id);
      }

      selectedVariantCallback(variant);
      return;
    }

    const createShopifySelection = () => new Shopify.OptionSelectors(`product-${productData.id}`, {
      product: productData,
      onVariantSelected: selectedVariantCallback,
      enableHistoryState: true,
    });

    createShopifySelection();

    // Wait for an explicit date pick (or ?variant=) before enabling ATC / BIS.
    resetEventPurchaseState();
  };

  const initEventOptionSelection = () => {
    if (!isMultiDateEvent || !eventDateBtns.length) return;

    const trueOptionSelectors = node.querySelectorAll('.single-option-selector');
    const trueDateSelector = Array.from(trueOptionSelectors).find(
      (selector) => parseInt(selector.dataset.option.replace('option', ''), 10) === dateOptionPosition,
    );

    const setActiveDateBtn = (activeBtn) => {
      eventDateBtns.forEach((btn) => {
        btn.classList.toggle('product__size-value-button--active', btn === activeBtn);
      });
    };

    const selectDate = (btn) => {
      if (!trueDateSelector) return;

      const { value } = btn.dataset;

      setActiveDateBtn(btn);

      if (selectedEventDate) {
        selectedEventDate.textContent = value;
        selectedEventDate.classList.add('product__selected-option--settled');
      }

      trueDateSelector.value = value;
      trueOptionSelectors.forEach((selector) => {
        selector.dispatchEvent(new Event('change', { bubbles: true }));
      });
    };

    eventDateBtns.forEach((btn) => {
      btn.addEventListener('click', () => selectDate(btn));
    });

    // Only preselect when a variant is linked in the URL (shared link / refresh).
    const updateSelectionFromVariantParam = () => {
      const params = new URLSearchParams(window.location.search);
      const variantParam = parseInt(params.get('variant'), 10);

      if (!variantParam) return;

      const variant = productData.variants.find((v) => v.id === variantParam);
      if (!variant) return;

      const date = variant[`option${dateOptionPosition}`];
      const matchingDateBtn = Array.from(eventDateBtns).find((btn) => btn.dataset.value === date);

      if (matchingDateBtn) {
        selectDate(matchingDateBtn);
      }
    };

    updateSelectionFromVariantParam();

    // Fall back to the next bookable date so the ATC button is never hidden.
    if (!hasSelectedEventDate()) {
      const dateBtns = Array.from(eventDateBtns);
      const firstBookable = dateBtns.find((btn) => !btn.hasAttribute('data-sold-out'));
      const fallbackBtn = firstBookable || dateBtns[0];

      if (fallbackBtn) {
        selectDate(fallbackBtn);
      }
    }
  };

  const initEventForm = () => {
    if (!form || !variantSelect) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (!hasSelectedEventDate()) {
        const dateList = node.querySelector('.js-eventDateList');
        dateList?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      toggleAddToCartBtn(formAddToCartBtn, false);

      const variantId = variantSelect.value;

      addVariant({
        variantId,
        quantity: 1,
        callback: () => toggleAddToCartBtn(formAddToCartBtn, true),
      });
    });
  };

  const storeProduct = () => {
    const { handle } = productData;
    const handles = localStorage.getItem('viewedProducts')?.split(',') || [];

    if (handles.includes(handle)) {
      const index = handles.indexOf(handle);
      handles.splice(index, 1);
    }

    handles.unshift(handle);
    localStorage.setItem('viewedProducts', handles);
  };

  const initMobileCta = () => {
    if (!mobileCta) return;

    // Show and hide mobile CTA
    const observer = new IntersectionObserver((entries) => {
      const rect = entries[0].boundingClientRect;
      const show = rect.y + rect.height < 0;

      mobileCta.classList[show ? 'add' : 'remove']('product__mobile-cta--active');
    });

    observer.observe(node);

    // Update mobile CTA top position based on header
    ctx.on('mobileHeader:scrolled', ({ mobileHeaderVisible }) => {
      mobileCta.classList[mobileHeaderVisible ? 'add' : 'remove']('product__mobile-cta--offset');
    });

    mobileCta.addEventListener('click', () => {
      if (isProductEvent) {
        if (!hasSelectedEventDate()) {
          const dateList = node.querySelector('.js-eventDateList');
          dateList?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        if (formAddToCartBtn && !formAddToCartBtn.hidden) {
          formAddToCartBtn.click();
        }

        return;
      }

      if (fitSizeSelectionBtn && !fitSizeSelectionBtn.hidden) {
        fitSizeSelectionBtn.click();
        return;
      }

      if (formAddToCartBtn && !formAddToCartBtn.hidden) {
        formAddToCartBtn.click();
      }
    });
  };

  async function init() {
    if (prelaunch === false && isProductEvent === false) {
      await initVariantSelection();

      if (sizeSelector === 'simple') {
        initSimpleOptionSelection();
      } else {
        initOptionSelection();
      }

      if (fitSizeAddToCartBtn && formAddToCartBtn) {
        fitSizeAddToCartBtn.addEventListener('click', () => {
          if (fitSizeAddToCartBtn.classList.contains('product__size-selector-atc--notify')) {
            const bis = fitSizeSelectionDrawer.querySelector('.js-klaviyoBIS')
              || node.querySelector('.js-klaviyoBIS');

            bis?.click();
            return;
          }

          formAddToCartBtn.click();
        });
      }

      if (!isSelectionComplete()) {
        setDrawerPurchaseButton(null);
      }

      initSizeGuideTriggers();
      initStoreTooltips();
      initForm();
    }

    if (isProductEvent === true) {
      initEventVariantSelection();
      initEventOptionSelection();
      initEventForm();
    }

    if (fitInfoBtn) {
      fitInfoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        displayFitInfo();
      });
    }

    initSwatches();
    initThumbnailDrawer();
    storeProduct();
    initMobileCta();

    node.dispatchEvent(new Event('loaded', { bubbles: true }));
  }

  /**
   * Init
   */
  await init();
});
